import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const CartPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], subtotal: 0, making_charges_gst: 0, total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchCart();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      setCart(response.data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    try {
      await axios.put(`${API}/cart/${productId}?quantity=${newQuantity}`);
      fetchCart();
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/${productId}`);
      fetchCart();
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleCheckout = () => {
    if (!user) {
      toast.error("Please login to checkout");
      navigate("/login");
      return;
    }
    navigate("/checkout");
  };

  if (!user) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="cart-login-prompt">
          <ShoppingBag className="w-16 h-16 text-[#737373] mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Your Cart</h1>
          <p className="text-[#A3A3A3] mb-8">Please login to view your cart</p>
          <Link to="/login" className="btn-gold inline-flex items-center gap-2">
            Login to Continue <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-[#121212] h-32" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (cart.items.length === 0) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-testid="empty-cart">
          <ShoppingBag className="w-16 h-16 text-[#737373] mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Your Cart is Empty</h1>
          <p className="text-[#A3A3A3] mb-8">Browse our collection and add some precious items</p>
          <Link to="/products" className="btn-gold inline-flex items-center gap-2">
            Shop Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cart-page">
        <h1 className="text-4xl font-bold text-white mb-8">Your Cart</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.items.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#121212] border border-[rgba(255,255,255,0.05)] p-4 flex gap-4"
                data-testid={`cart-item-${item.id}`}
              >
                <Link to={`/product/${item.id}`}>
                  <img 
                    src={item.image_url}
                    alt={item.name}
                    className="w-24 h-24 object-cover bg-[#0a0a0a]"
                  />
                </Link>
                
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className={`badge-${item.metal_type} text-xs`}>
                        {item.purity}
                      </span>
                      <Link to={`/product/${item.id}`}>
                        <h3 className="text-white font-medium mt-1 hover:text-[#D4AF37] transition-colors">
                          {item.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-[#737373]">
                        {item.weight}{item.weight_unit}
                      </p>
                    </div>
                    <button 
                      onClick={() => removeItem(item.id)}
                      className="text-[#737373] hover:text-[#FF3B30] transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex justify-between items-end mt-4">
                    {/* Quantity */}
                    <div className="flex items-center gap-2 bg-[#0a0a0a] border border-[rgba(255,255,255,0.1)] p-1">
                      <button 
                        onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                        className="p-1.5 hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-8 text-center text-sm text-white">{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1.5 hover:bg-[rgba(255,255,255,0.05)] transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                      <p className={`price-tag ${item.metal_type} text-xl font-medium`}>
                        ₹{item.item_total?.toLocaleString()}
                      </p>
                      <p className="text-xs text-[#737373]">
                        ₹{item.total_price?.toLocaleString()} × {item.quantity}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.05)] p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-lg font-semibold text-white mb-6">Order Summary</h2>

              <div className="space-y-3 mb-6">
                <div className="billing-row">
                  <span className="text-[#A3A3A3]">Subtotal (Live Metal Value)</span>
                  <span className="text-white">₹{cart.subtotal?.toLocaleString()}</span>
                </div>
                <div className="billing-row">
                  <span className="text-[#A3A3A3]">Making Charges & GST</span>
                  <span className="text-white">₹{cart.making_charges_gst?.toLocaleString()}</span>
                </div>
                <div className="billing-row">
                  <span className="text-[#A3A3A3]">Shipping</span>
                  <span className="text-[#00E676]">FREE</span>
                </div>
                <div className="billing-row total">
                  <span className="text-white">Total</span>
                  <span className="text-[#D4AF37] text-2xl font-light" data-testid="cart-total">
                    ₹{cart.total?.toLocaleString()}
                  </span>
                </div>
              </div>

              <p className="text-xs text-[#737373] mb-6">
                * Prices are updated every 2 minutes. Final price will be locked at checkout.
              </p>

              <Button
                onClick={handleCheckout}
                className="w-full btn-gold flex items-center justify-center gap-2"
                data-testid="checkout-btn"
              >
                Proceed to Checkout <ArrowRight className="w-4 h-4" />
              </Button>

              <Link 
                to="/products" 
                className="block text-center text-sm text-[#A3A3A3] hover:text-[#D4AF37] mt-4 transition-colors"
              >
                Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CartPage;
